/**
 * First-login language picker on the tenant dashboard (COL-478).
 *
 * Shows once, to a plain tenant account, while `profiles.language_chosen_at`
 * is NULL. Choosing or dismissing stamps
 * the profile, so the prompt never returns — not even on another device.
 * Deliberately no localStorage: the profile is the only "was asked" record.
 *
 * Not shown to staff, owner or operator accounts — they can switch from the
 * header whenever they like, so a prompt about it is noise — nor while an
 * admin is acting as the tenant: the admin's click must not decide the
 * tenant's language.
 *
 * Owns its profile read (two columns) instead of riding on the dashboard's
 * profile query: if the columns are not there yet — a frontend deploy that
 * lands minutes before its migration — this component hides itself and the
 * rest of the dashboard is untouched.
 *
 * Dismiss keeps whatever language the dashboard is currently in (German by
 * default) rather than forcing German: a tenant who already switched this
 * browser to English and closes the prompt expects English to stay.
 *
 * Each option is labelled in its own language on purpose (LANGUAGE_LABELS):
 * the person reading this has not told us which language they read yet.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  LANGUAGE_LABELS,
  dirFor,
  type SupportedLanguage,
} from "@/lib/i18n/languages";
import { selectableLanguages } from "@/lib/i18n/tenant-languages";

/** Query key shared with Settings → Language so a choice there closes the prompt. */
export const languagePromptQueryKey = (userId: string) =>
  ["tenant-language-prompt", userId] as const;

export function TenantLanguagePicker() {
  const { language, setLanguage, t } = useLanguage();
  const {
    user,
    rolesReady,
    hasStaffRole,
    hasOwnerRole,
    hasOperatorRole,
    isImpersonating,
  } = useAuth();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  // Closed for this session after a save attempt (success or failure), so a
  // failed write does not turn into a prompt that re-opens on every render.
  const [settled, setSettled] = useState(false);

  // The PROMPT stays tenant-only — it is onboarding, and nagging staff about
  // a choice they can make from the header at any time is noise. WHICH
  // languages it lists is the shared rule.
  const isTenantAccount = !hasStaffRole && !hasOwnerRole && !hasOperatorRole;
  // The dialog only mounts on the tenant dashboard, a surface that renders
  // all six languages — a full-menu surface by construction.
  const options = selectableLanguages({
    isSignedIn: !!user,
    surfaceOffersAll: true,
  });
  const userId = user?.id;

  const eligible =
    !settled && rolesReady && isTenantAccount && !isImpersonating;

  const { data: chosenAt } = useQuery({
    queryKey: languagePromptQueryKey(userId ?? ""),
    enabled: eligible && !!userId,
    // `undefined` = unknown (still loading or failed) → never show;
    // `null` = asked never → show; a timestamp → already answered.
    queryFn: async (): Promise<string | null | undefined> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("language_chosen_at")
        .eq("id", userId!)
        .maybeSingle();
      if (error || !data) return undefined;
      return data.language_chosen_at;
    },
    staleTime: 60_000,
    retry: false,
  });

  const open = eligible && !!userId && chosenAt === null;

  const save = async (next: SupportedLanguage, apply: boolean) => {
    if (!userId || busy) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          preferred_language: next,
          language_chosen_at: new Date().toISOString(),
        })
        .eq("id", userId);
      if (error) throw error;
      // Legacy mirror, best effort — the same one Settings → Language writes.
      // notify-property-decision still reads user_metadata.preferred_language
      // until COL-480 moves every email onto the profiles column, so without
      // this a choice made here would change the dashboard but not the emails
      // the copy above promises, while the identical choice made in Settings
      // would change both. Never allowed to fail the save: the profile row is
      // already written and is the source of truth.
      try {
        await supabase.auth
          .updateUser({ data: { preferred_language: next } })
          .catch(() => undefined);
      } catch {
        // ignore
      }
      if (apply) setLanguage(next);
      queryClient.invalidateQueries({
        queryKey: languagePromptQueryKey(userId),
      });
    } catch {
      toast.error(
        t(
          "dashboard.languagePicker.saveFailed",
          "We could not save your language. You can set it later in Settings."
        )
      );
    } finally {
      setBusy(false);
      setSettled(true);
    }
  };

  // Closing by any other means (Escape, backdrop) counts as "keep current".
  const dismiss = () => void save(language, false);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) dismiss();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Globe className="h-6 w-6" aria-hidden />
          </div>
          <DialogTitle className="text-center">
            {t("dashboard.languagePicker.title", "Choose your language")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t(
              "dashboard.languagePicker.desc",
              "coliviq will use it for your dashboard and your emails. You can change it any time in Settings."
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          className="grid grid-cols-2 gap-3 py-2"
          role="radiogroup"
          aria-label={t(
            "dashboard.languagePicker.title",
            "Choose your language"
          )}
        >
          {options.map((code) => {
            const selected = code === language;
            return (
              <button
                key={code}
                type="button"
                role="radio"
                aria-checked={selected}
                lang={code}
                dir={dirFor(code)}
                disabled={busy}
                onClick={() => void save(code, true)}
                className={cn(
                  "flex h-14 items-center justify-between rounded-xl border px-4 text-base font-medium transition-colors",
                  "hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  selected
                    ? "border-primary bg-primary/5 text-foreground"
                    : "border-border text-foreground",
                  busy && "opacity-60"
                )}
              >
                <span>{LANGUAGE_LABELS[code]}</span>
                {selected && (
                  <Check className="h-4 w-4 text-primary" aria-hidden />
                )}
              </button>
            );
          })}
        </div>

        <DialogFooter className="sm:justify-center">
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={dismiss}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              t("dashboard.languagePicker.dismiss", "Keep current language")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TenantLanguagePicker;
